<template>
	<div class="page">
		<div ref="mountEl" class="view"></div>
		<div class="panel">
			<div class="row">
				<button :style="moveBtnStyle('U')" @click="doMoveView('U')">U</button>
				<button :style="moveBtnStyle('U\'')" @click="doMoveView('U\'')">U'</button>
				<button :style="moveBtnStyle('D')" @click="doMoveView('D')">D</button>
				<button :style="moveBtnStyle('D\'')" @click="doMoveView('D\'')">D'</button>
			</div>
			<div class="row">
				<button :style="moveBtnStyle('L')" @click="doMoveView('L')">L</button>
				<button :style="moveBtnStyle('L\'')" @click="doMoveView('L\'')">L'</button>
				<button :style="moveBtnStyle('R')" @click="doMoveView('R')">R</button>
				<button :style="moveBtnStyle('R\'')" @click="doMoveView('R\'')">R'</button>
			</div>
			<div class="row">
				<button :style="moveBtnStyle('F')" @click="doMoveView('F')">F</button>
				<button :style="moveBtnStyle('F\'')" @click="doMoveView('F\'')">F'</button>
				<button :style="moveBtnStyle('B')" @click="doMoveView('B')">B</button>
				<button :style="moveBtnStyle('B\'')" @click="doMoveView('B\'')">B'</button>
			</div>

			<div class="row">
				<button @click="scramble()">Scramble</button>
				<button @click="resetCube()">Reset</button>
			</div>

			<div class="hint">
				鼠标左键拖拽旋转视角，滚轮缩放。按钮可转动各层。
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as THREE from 'three'
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js'
import { COLORS } from './Colors.js'

// 颜色映射到按钮背景
const FACE_COLOR = {
	U: COLORS.WHITE, D: COLORS.YELLOW, L: COLORS.ORANGE, R: COLORS.RED, F: COLORS.GREEN, B: COLORS.BLUE
}

// 用于强制刷新按钮颜色
const moveBtnKey = ref(0)

function moveBtnStyle(move) {
	// 利用 key 触发更新
	moveBtnKey.value
	const { top, front } = getTopAndFrontFace()
	const realFaces = getCubeRealFaces(top, front)
	const color = FACE_COLOR[realFaces[move.replace("'", "")]] || 'rgba(255,255,255,0.1)'
	return {
		color: color,
	}
}

// 根据视角映射move
async function doMoveView(move) {
	const { top, front } = getTopAndFrontFace()

	const realMove = mapMoveToCurrentOrientation(move, top, front)

	if (realMove) {
		await doMove(realMove)
		// 强制刷新按钮颜色
		moveBtnKey.value++
		await nextTick()
	}
}

const mountEl = ref(null)

let scene, camera, renderer, controls, rafId
let cubeRoot // 魔方整体容器
let isAnimating = false

// 魔方参数
// const N = 3
const coords = [-1, 0, 1]
const cubieSize = 1
const gap = 0.06
const step = cubieSize + gap

// 存每个小块：mesh + 逻辑坐标(i,j,k)
const cubies = []

// 颜色（按常见配色）
const COL = {
	U: COLORS.WHITE, // Up: 白
	D: COLORS.YELLOW, // Down: 黄
	L: COLORS.ORANGE, // Left: 橙
	R: COLORS.RED, // Right: 红
	F: COLORS.GREEN, // Front: 绿
	B: COLORS.BLUE, // Back: 蓝
	X: COLORS.BLACK, // 内部/无色：深灰
}

// 立方体六面与世界坐标轴的关系
// const FACE_AXIS = {
// 	U: { axis: 'y', dir: 1 },
// 	D: { axis: 'y', dir: -1 },
// 	F: { axis: 'z', dir: 1 },
// 	B: { axis: 'z', dir: -1 },
// 	R: { axis: 'x', dir: 1 },
// 	L: { axis: 'x', dir: -1 },
// }

// 根据相机朝向，判断当前视角下的顶面和前面颜色（面名）
function getTopAndFrontFace() {
	// 防止 camera 未初始化时报错，返回默认
	if (!camera) return { top: 'U', front: 'F' }

	// 立方体中心在 (0,0,0)，六面中心点
	const faceCenters = {
		U: new THREE.Vector3(0, 1, 0),
		D: new THREE.Vector3(0, -1, 0),
		F: new THREE.Vector3(0, 0, 1),
		B: new THREE.Vector3(0, 0, -1),
		R: new THREE.Vector3(1, 0, 0),
		L: new THREE.Vector3(-1, 0, 0),
	}

	// 1. front: 距离相机最近的面
	let minDist = Infinity, frontFace = 'F'
	Object.entries(faceCenters).forEach(([face, v]) => {
		const dist = camera.position.distanceTo(v)
		if (dist < minDist) {
			minDist = dist
			frontFace = face
		}
	})

	// 2. top: 投影到屏幕后y最小的面
	// const width = renderer.domElement.width
	const height = renderer.domElement.height
	let minY = Infinity, topFace = 'U'

	// 2. top: 只在与 front 不同且不相对的面中，选屏幕最上方
	const opposite = { U: 'D', D: 'U', L: 'R', R: 'L', F: 'B', B: 'F' }
	Object.entries(faceCenters).forEach(([face, v]) => {
		if (face === frontFace || face === opposite[frontFace]) return
		const p = v.clone().project(camera)
		const screenY = (1 - p.y) * 0.5 * height
		if (screenY < minY) {
			minY = screenY
			topFace = face
		}
	})

	console.log('Top/Front:', topFace, frontFace)

	return { top: topFace, front: frontFace }
}


function getCubeRealFaces(top, front) {
	const faces = ['U', 'D', 'L', 'R', 'F', 'B'];

	// 每个面的方向向量
	const dir = {
		U: [0, 1, 0],
		D: [0, -1, 0],
		F: [0, 0, 1],
		B: [0, 0, -1],
		R: [1, 0, 0],
		L: [-1, 0, 0],
	};

	const up = dir[top];
	const frontDir = dir[front];

	if (!up || !frontDir) {
		throw new Error('Invalid face label');
	}

	// top 和 front 不能相对或相同
	// const dot =
	//   up[0] * frontDir[0] +
	//   up[1] * frontDir[1] +
	//   up[2] * frontDir[2];

	// if (dot !== 0) {
	//   throw new Error('top and front must be perpendicular');
	// }

	// 叉积：right = up × front
	const right = [
		up[1] * frontDir[2] - up[2] * frontDir[1],
		up[2] * frontDir[0] - up[0] * frontDir[2],
		up[0] * frontDir[1] - up[1] * frontDir[0],
	];

	// 反方向工具
	const neg = ([x, y, z]) => [-x, -y, -z];

	// 向量 → 面
	function dirToFace(v) {
		return faces.find(
			f =>
				dir[f][0] === v[0] &&
				dir[f][1] === v[1] &&
				dir[f][2] === v[2]
		);
	}

	return {
		U: top,
		D: dirToFace(neg(up)),
		F: front,
		B: dirToFace(neg(frontDir)),
		R: dirToFace(right),
		L: dirToFace(neg(right)),
	};
}

// 根据当前top/front和用户输入的标准move，输出实际doMove参数
// 例如：用户点击U，实际应转动当前top对应的面
function mapMoveToCurrentOrientation(move, top, front) {
	// 魔方标准朝向：top=U, front=F
	// 其它朝向时，move要映射到实际面
	// 六面顺序：U D F B R L
	// 以top/front唯一确定朝向
	// 构建一个面变换表

	const realFaces = getCubeRealFaces(top, front)
	const realMove = realFaces[move.replace("'", "")] + (move.includes("'") ? "'" : "")
	return realMove
}

function makeCubieMaterials(i, j, k) {
	// Three.js BoxGeometry 的面顺序：
	// 0:+X, 1:-X, 2:+Y, 3:-Y, 4:+Z, 5:-Z
	const mats = []
	mats[0] = new THREE.MeshStandardMaterial({ color: i === 1 ? COL.R : COL.X })
	mats[1] = new THREE.MeshStandardMaterial({ color: i === -1 ? COL.L : COL.X })
	mats[2] = new THREE.MeshStandardMaterial({ color: j === 1 ? COL.U : COL.X })
	mats[3] = new THREE.MeshStandardMaterial({ color: j === -1 ? COL.D : COL.X })
	mats[4] = new THREE.MeshStandardMaterial({ color: k === 1 ? COL.F : COL.X })
	mats[5] = new THREE.MeshStandardMaterial({ color: k === -1 ? COL.B : COL.X })
	return mats
}

function buildCube() {
	cubies.length = 0
	cubeRoot.clear()

	const geom = new THREE.BoxGeometry(cubieSize, cubieSize, cubieSize)

	for (const i of coords) {
		for (const j of coords) {
			for (const k of coords) {
				const mats = makeCubieMaterials(i, j, k)
				const mesh = new THREE.Mesh(geom, mats)

				mesh.position.set(i * step, j * step, k * step)
				mesh.castShadow = true
				mesh.receiveShadow = true

				cubeRoot.add(mesh)
				cubies.push({
					mesh,
					i, j, k, // 逻辑坐标（-1/0/1）
				})
			}
		}
	}
}

// 将某层 cubies 挂到临时 group，旋转结束再“烘焙”回去
async function rotateLayer(axis, layerIndex, dir) {
	if (isAnimating) return
	isAnimating = true

	// 选中该层的小块
	const selected = cubies.filter(c => {
		if (axis === 'x') return c.i === layerIndex
		if (axis === 'y') return c.j === layerIndex
		return c.k === layerIndex // 'z'
	})

	// 临时 group
	const pivot = new THREE.Group()
	cubeRoot.add(pivot)

	// 把选中 mesh 迁移到 pivot（保持世界坐标）
	selected.forEach(c => {
		THREE.SceneUtils?.detach?.(c.mesh, cubeRoot, scene) // 旧版可能有
	})
	// 不依赖 SceneUtils：用 attach 保持世界坐标
	selected.forEach(c => pivot.attach(c.mesh))

	// 目标角度：90°
	const target = (Math.PI / 2) * dir
	let current = 0
	const speed = 0.14 // 每帧增量（可调）

	// 动画循环（用 RAF）
	await new Promise(resolve => {
		const tick = () => {
			const delta = Math.min(speed, Math.abs(target - current))
			current += Math.sign(target) * delta

			if (axis === 'x') pivot.rotation.x = current
			if (axis === 'y') pivot.rotation.y = current
			if (axis === 'z') pivot.rotation.z = current

			// 到位
			if (Math.abs(target - current) < 1e-6) {
				// 烘焙：把旋转应用到每个 mesh 的世界变换上
				pivot.updateMatrixWorld(true)

				selected.forEach(c => {
					// 先把 mesh 放回 cubeRoot（保持世界变换）
					cubeRoot.attach(c.mesh)

					// 对齐位置，避免浮点误差
					c.mesh.position.x = Math.round(c.mesh.position.x / step) * step
					c.mesh.position.y = Math.round(c.mesh.position.y / step) * step
					c.mesh.position.z = Math.round(c.mesh.position.z / step) * step

					// 更新逻辑坐标
					c.i = Math.round(c.mesh.position.x / step)
					c.j = Math.round(c.mesh.position.y / step)
					c.k = Math.round(c.mesh.position.z / step)
				})

				// 移除 pivot
				cubeRoot.remove(pivot)
				resolve()
				return
			}

			rafId = requestAnimationFrame(tick)
		}
		tick()
	})

	isAnimating = false
}

function parseMove(move) {
	// 标准：U/D = y轴层；L/R = x轴层；F/B = z轴层
	// dir：顺/逆。这里定义为：从该面的“外侧看过去”顺时针为正。
	const prime = move.includes("'")
	const m = move.replace("'", "")

	switch (m) {
		case 'U': return { axis: 'y', layer: 1, dir: prime ? -1 : 1 }
		case 'D': return { axis: 'y', layer: -1, dir: prime ? 1 : -1 }
		case 'L': return { axis: 'x', layer: -1, dir: prime ? -1 : 1 }
		case 'R': return { axis: 'x', layer: 1, dir: prime ? 1 : -1 }
		case 'F': return { axis: 'z', layer: 1, dir: prime ? -1 : 1 }
		case 'B': return { axis: 'z', layer: -1, dir: prime ? 1 : -1 }
		default: return null
	}
}

async function doMove(move) {
	const p = parseMove(move)
	if (!p) return
	await rotateLayer(p.axis, p.layer, p.dir)
}

async function scramble() {
	const moves = ['U', "U'", 'D', "D'", 'L', "L'", 'R', "R'", 'F', "F'", 'B', "B'"]
	// 20 步打乱
	for (let t = 0; t < 20; t++) {
		const m = moves[Math.floor(Math.random() * moves.length)]
		// eslint-disable-next-line no-await-in-loop
		await doMove(m)
	}
}

function resetCube() {
	if (isAnimating) return
	// 清空后重建（最简单稳定）
	buildCube()
}

function initThree() {
	scene = new THREE.Scene()
	scene.background = new THREE.Color(0x0b0f14)

	const w = mountEl.value.clientWidth
	const h = mountEl.value.clientHeight

	camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100)
	camera.position.set(6, 6, 8)

	renderer = new THREE.WebGLRenderer({ antialias: true })
	renderer.setSize(w, h)
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
	renderer.shadowMap.enabled = true

	mountEl.value.appendChild(renderer.domElement)

	controls = new TrackballControls(camera, renderer.domElement)
	controls.noPan = true
	controls.noZoom = false
	controls.staticMoving = false // 允许惯性
	controls.dynamicDampingFactor = 0.28 // 增强惯性，手感更丝滑
	controls.minPolarAngle = 0.1 // 略高于水平，防止 tilt
	controls.maxPolarAngle = Math.PI - 0.1 // 略低于倒置，防止 tilt
	controls.rotateSpeed = 2.8 // 提高旋转速度（默认1.0）
	controls.zoomSpeed = 1.4 // 提高缩放速度（默认1.2）
	controls.panSpeed = 0.9 // 提高平移速度（默认0.3）
	controls.addEventListener('change', () => {
		// 强制刷新按钮颜色
		moveBtnKey.value++
	})

	// 光照
	const hemi = new THREE.HemisphereLight(0xffffff, 0x223344, 0.85)
	scene.add(hemi)

	const dir = new THREE.DirectionalLight(0xffffff, 0.9)
	dir.position.set(6, 10, 8)
	dir.castShadow = true
	scene.add(dir)

	const amb = new THREE.AmbientLight(0xffffff, 0.5)
	scene.add(amb)

	// 地面（可选）
	// const ground = new THREE.Mesh(
	//   new THREE.PlaneGeometry(40, 40),
	//   new THREE.MeshStandardMaterial({ color: 0x0a0c10, roughness: 1 })
	// )
	// ground.rotation.x = -Math.PI / 2
	// ground.position.y = -4
	// ground.receiveShadow = true
	// scene.add(ground)

	cubeRoot = new THREE.Group()
	scene.add(cubeRoot)
	buildCube()

	const onResize = () => {
		const w2 = mountEl.value.clientWidth
		const h2 = mountEl.value.clientHeight
		camera.aspect = w2 / h2
		camera.updateProjectionMatrix()
		renderer.setSize(w2, h2)
	}
	window.addEventListener('resize', onResize)

	const animate = () => {
		controls.update()
		renderer.render(scene, camera)
		rafId = requestAnimationFrame(animate)
	}
	animate()

	return () => {
		window.removeEventListener('resize', onResize)
	}
}

let disposeResize
onMounted(() => {
	disposeResize = initThree()
})

onBeforeUnmount(() => {
	if (rafId) cancelAnimationFrame(rafId)
	if (disposeResize) disposeResize()
	controls?.dispose?.()
	renderer?.dispose?.()
	if (renderer?.domElement?.parentNode) {
		renderer.domElement.parentNode.removeChild(renderer.domElement)
	}
})
</script>

<style scoped>
.page {
	display: grid;
	grid-template-columns: 1fr 260px;
	gap: 12px;
	height: 100vh;
	padding: 12px;
	box-sizing: border-box;
	background: #070a0f;
}

.view {
	border-radius: 14px;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.08);
	min-height: 520px;
}

.panel {
	border-radius: 14px;
	padding: 12px;
	border: 1px solid rgba(255, 255, 255, 0.08);
	background: rgba(255, 255, 255, 0.03);
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}

button {
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid rgba(255, 255, 255, 0.12);
	background: rgba(255, 255, 255, 0.06);
	color: white;
	cursor: pointer;
	font-size: 14px;
}

button:hover {
	background: rgba(255, 255, 255, 0.10);
}

.hint {
	margin-top: 6px;
	color: rgba(255, 255, 255, 0.7);
	font-size: 12px;
	line-height: 1.35;
}
</style>
